import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Sidebar from '../components/Sidebar';
import axios from 'axios';
import common from '../utils/common';
import { authStorage } from '../utils/authStorage';
import { fetchParticipants } from '../features/api/apiSlice';
import './ntc-branding.css';

const Documents = () => {
  const dispatch = useAppDispatch();
  const { participants: reduxParticipants } = useAppSelector((state) => state.api);
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loginData, setLoginData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [originalProjects, setOriginalProjects] = useState([]);
  const [projectsWithDocuments, setProjectsWithDocuments] = useState(new Set());
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    document: null
  });
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);

  // Custom Alert State
  const [customAlert, setCustomAlert] = useState({
    show: false,
    message: '',
    type: 'error'
  });

  const showAlert = (message, type = 'error') => {
    setCustomAlert({ show: true, message, type });
    setTimeout(() => {
      setCustomAlert({ show: false, message: '', type: 'error' });
    }, type === 'success' ? 4000 : 5000);
  };

  const hideAlert = () => {
    setCustomAlert({ show: false, message: '', type: 'error' });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
    document.documentElement.classList.toggle('minimenu');
    localStorage.setItem('nexel-classic-dashboard-menu-mini-theme', !sidebarCollapsed ? 'menu-mini-theme' : '');
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('nexel-classic-dashboard-menu-mini-theme');
    if (savedTheme === 'menu-mini-theme') {
      setSidebarCollapsed(true);
      document.documentElement.classList.add('minimenu');
    } else {
      setSidebarCollapsed(false);
      document.documentElement.classList.remove('minimenu');
    }

    // Prevent default drag behavior globally to avoid browser opening files
    const handleGlobalDragOver = (e) => {
      e.preventDefault();
    };

    const handleGlobalDrop = (e) => {
      e.preventDefault();
    };

    document.addEventListener('dragover', handleGlobalDragOver);
    document.addEventListener('drop', handleGlobalDrop);

    return () => {
      document.removeEventListener('dragover', handleGlobalDragOver);
      document.removeEventListener('drop', handleGlobalDrop);
    };
  }, []);

  useEffect(() => {
    const data = authStorage.getLoginData();
    if (!data) return;
    if (authStorage.getDirectLink() === '1') {
      window.location.href = '/';
      return;
    }
    setLoginData(data);
  }, []);

  useEffect(() => {
    dispatch(fetchParticipants());
  }, [dispatch]);

  useEffect(() => {
    if (reduxParticipants) {
      setParticipants(reduxParticipants);
    }
  }, [reduxParticipants]);

  useEffect(() => {
    if (!loginData) return;
    
    const baseUrl = common.Data.API_URL;
    const url = window.location.href;
    const projectNameParam = url.split('?')[1];
    
    axios.get(`${baseUrl}/v1/get-projects/all-${loginData.id}-${loginData.role}`)
      .then((response) => {
        const projectsData = response.data || [];
        console.log('All projects fetched:', projectsData);
        console.log('Looking for Kilt Protocol:', projectsData.find(p => p.name.toLowerCase().includes('kilt')));
        setProjects(projectsData);
        setOriginalProjects(projectsData);
        
        if (projectsData.length > 0) {
          let selected = projectsData[0];
          if (projectNameParam) {
            const found = projectsData.find(p => p.name.toLowerCase() === decodeURIComponent(projectNameParam).toLowerCase());
            if (found) selected = found;
          }
          // Set selected project immediately so UI reflects the selection
          setSelectedProject(selected);
          // Load documents for the selected project
          populateDocuments(selected);
        }
      })
      .catch((error) => {
        console.error('Error fetching projects:', error);
        showAlert('Failed to fetch projects', 'error');
      });
  }, [loginData]);

  // Filter projects based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setProjects(originalProjects);
      return;
    }
    
    const filtered = originalProjects.filter(project => 
      project.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setProjects(filtered);
    
    // If selected project is filtered out, select first filtered project
    if (filtered.length > 0 && selectedProject && !filtered.find(p => p.id === selectedProject.id)) {
      const firstFiltered = filtered[0];
      populateDocuments(firstFiltered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, originalProjects]);

  const populateDocuments = (project) => {
    if (!project) {
      setSelectedProject(null);
      setDocuments([]);
      setLoadingDocuments(false);
      return Promise.resolve();
    }
    
    // Set selected project immediately if not already set
    setSelectedProject(prev => prev?.id !== project.id ? project : prev);
    setLoadingDocuments(true);
    const baseUrl = common.Data.API_URL;
    return axios.get(`${baseUrl}/v1/get-files/${project.id}`)
      .then((response) => {
        // Handle various response structures from get-files endpoint
        let docs = [];
        if (Array.isArray(response.data)) {
          docs = response.data;
        } else if (response.data?.files) {
          docs = Array.isArray(response.data.files) ? response.data.files : [];
        } else if (response.data?.documents) {
          docs = Array.isArray(response.data.documents) ? response.data.documents : [];
        } else if (response.data?.data) {
          docs = Array.isArray(response.data.data) ? response.data.data : [];
        } else if (response.data?.result) {
          docs = Array.isArray(response.data.result) ? response.data.result : [];
        }
        
        console.log(`Raw response for ${project.name} (ID: ${project.id}):`, response.data);
        console.log(`Documents extracted:`, docs);
        console.log(`Total documents found: ${docs.length}`);
        
        // Filter out any invalid documents and normalize field names
        const validDocs = docs
          .filter(doc => doc && (doc.document_url || doc.url || doc.file_url || doc.file_path))
          .map(doc => ({
            id: doc.id || doc.file_id || doc.document_id,
            document_name: doc.document_name || doc.name || doc.file_name || doc.filename || 'Untitled',
            document_url: doc.document_url || doc.url || doc.file_url || doc.file_path,
            document_type: doc.document_type || doc.type || doc.file_type || 'Other',
            description: doc.description || '',
            created_at: doc.created_at || doc.created_date || doc.uploaded_at || doc.date
          }));
        
        console.log(`Valid documents after processing:`, validDocs);
        setDocuments(validDocs);
        setSelectedProject(project);
        setLoadingDocuments(false);
        
        // Track projects with documents - ensure orange dot stays visible
        if (validDocs.length > 0) {
          setProjectsWithDocuments(prev => new Set([...prev, project.id]));
        } else {
          // Only remove from set if project was not previously known to have documents
          // This ensures orange dot stays visible for projects that had documents
          setProjectsWithDocuments(prev => {
            // If project was already in the set (has documents), keep it there
            // This maintains the orange dot even if current fetch returns empty
            if (prev.has(project.id)) {
              return prev; // Keep the existing set
            }
            // If project wasn't in the set and has no docs, no change needed
            return prev;
          });
        }
      })
      .catch((error) => {
        console.error(`Error fetching documents for ${project.name} (ID: ${project.id}):`, error);
        console.error('Error status:', error.response?.status);
        console.error('Error status text:', error.response?.statusText);
        console.error('Error data:', error.response?.data);
        console.error('Error message:', error.message);
        console.error('Full error:', error);
        
        // If it's a 404 or the endpoint doesn't exist, just set empty documents
        // Don't show an error alert for missing documents endpoint
        if (error.response?.status === 404) {
          console.log(`No documents endpoint found for project ${project.name}, treating as empty`);
          setDocuments([]);
          setSelectedProject(project);
          setProjectsWithDocuments(prev => {
            const newSet = new Set(prev);
            newSet.delete(project.id);
            return newSet;
          });
        } else {
          // For other errors, still set empty but log the error
          setDocuments([]);
          setSelectedProject(project);
          setProjectsWithDocuments(prev => {
            const newSet = new Set(prev);
            newSet.delete(project.id);
            return newSet;
          });
          // Only show alert for non-404 errors
          if (error.response?.status !== 404) {
            showAlert(`Failed to load documents for ${project.name}. Please try again.`, 'error');
          }
        }
        setLoadingDocuments(false);
      });
  };

  // Check all projects for documents on load
  useEffect(() => {
    if (!loginData || projects.length === 0) return;
    
    const baseUrl = common.Data.API_URL;
    const checkPromises = projects.map(project => 
      axios.get(`${baseUrl}/v1/get-files/${project.id}`)
        .then(response => {
          // Handle various response structures
          let docs = [];
          if (Array.isArray(response.data)) {
            docs = response.data;
          } else if (response.data?.files) {
            docs = Array.isArray(response.data.files) ? response.data.files : [];
          } else if (response.data?.documents) {
            docs = Array.isArray(response.data.documents) ? response.data.documents : [];
          } else if (response.data?.data) {
            docs = Array.isArray(response.data.data) ? response.data.data : [];
          } else if (response.data?.result) {
            docs = Array.isArray(response.data.result) ? response.data.result : [];
          }
          
          // Check if there are valid documents
          const hasValidDocs = docs.some(doc => doc && (doc.document_url || doc.url || doc.file_url || doc.file_path));
          if (hasValidDocs) {
            return project.id;
          }
          return null;
        })
        .catch(() => null)
    );
    
    Promise.all(checkPromises).then(results => {
      const projectIdsWithDocs = results.filter(id => id !== null);
      setProjectsWithDocuments(new Set(projectIdsWithDocs));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, loginData]);

  // Reusable function to upload a file
  const uploadFile = async (file) => {
    if (!selectedProject || !loginData) {
      showAlert('Please select a project first', 'error');
      return;
    }

    if (!file) return;

    setUploadingFile(true);
    const baseUrl = common.Data.API_URL;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', selectedProject.id);
    formData.append('created_by', loginData.id);
    formData.append('document_name', file.name);
    formData.append('document_type', 'Other');

    try {
      const response = await axios.post(`${baseUrl}/v1/save-file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      showAlert('Document uploaded successfully', 'success');
      // Refresh documents and ensure orange dot is visible
      await populateDocuments(selectedProject);
      setProjectsWithDocuments(prev => new Set([...prev, selectedProject.id]));
    } catch (error) {
      console.error('Error uploading document:', error);
      showAlert('Failed to upload document', 'error');
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle file input change
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      await uploadFile(file);
      event.target.value = ''; // Reset file input
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploadingFile) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploadingFile) {
      setIsDragOver(true);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (uploadingFile || !selectedProject) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Upload the first file (can be extended to handle multiple files)
    const file = files[0];
    
    // Validate file type
    const validExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      showAlert(`File type not supported. Supported formats: ${validExtensions.join(', ')}`, 'error');
      return;
    }

    await uploadFile(file);
  };

  const handleDeleteClick = (doc) => {
    setDeleteConfirmation({
      show: true,
      document: doc
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmation.document || !selectedProject || !loginData) {
      showAlert('Missing document, project, or user information', 'error');
      setDeleteConfirmation({ show: false, document: null });
      return;
    }
    
    const doc = deleteConfirmation.document;
    const baseUrl = common.Data.API_URL;
    
    // Try to get document ID from multiple possible fields
    const docId = doc.id || doc.file_id || doc.document_id;
    
    if (!docId) {
      console.error('No document ID found:', doc);
      showAlert('Cannot delete document: Missing document ID', 'error');
      setDeleteConfirmation({ show: false, document: null });
      return;
    }
    
    // Set deleting state
    setDeletingDocumentId(docId);
    
    console.log('Attempting to delete document:', {
      docId,
      projectId: selectedProject.id,
      userId: loginData.id,
      documentName: doc.document_name
    });
    
    // Use the correct delete-file endpoint: /v1/delete-file/${file.id}/${logindata.id}
    try {
      console.log(`Attempting to delete file: ${docId} for user: ${loginData.id}`);
      const response = await axios.get(`${baseUrl}/v1/delete-file/${docId}/${loginData.id}`);
      console.log('Delete successful:', response);
      
      showAlert('Document deleted successfully', 'success');
      await populateDocuments(selectedProject);
      // Orange dot will remain visible even if all documents are deleted
      // This maintains consistency in the UI
    } catch (error) {
      console.error('Error deleting file:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          error.message || 
                          'Failed to delete document. Please try again.';
      showAlert(errorMessage, 'error');
    } finally {
      // Clear deleting state and close modal
      setDeletingDocumentId(null);
      setDeleteConfirmation({ show: false, document: null });
    }
  };

  const handleDeleteCancel = () => {
    // Only allow cancel if not currently deleting
    if (deletingDocumentId === null) {
      setDeleteConfirmation({ show: false, document: null });
    }
  };

  return (
    <div className="nxl-wrapper">
      {/* Custom Alert */}
      {customAlert.show && (
        <div 
          className="position-fixed"
          style={{
            top: '24px',
            right: '24px',
            zIndex: 9999,
            minWidth: '380px',
            maxWidth: '450px',
            animation: 'slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}
        >
          <div 
            className="card shadow-lg border-0"
            style={{
              borderRadius: '16px',
              overflow: 'hidden',
              border: `2px solid ${
                customAlert.type === 'error' ? '#dc3545' : 
                customAlert.type === 'success' ? '#28a745' : 
                customAlert.type === 'warning' ? '#ffc107' : 
                '#17a2b8'
              }`
            }}
          >
            <div 
              className={`card-body p-3 d-flex align-items-center position-relative`}
              style={{
                backgroundColor: '#ffffff',
                borderLeft: `5px solid ${
                  customAlert.type === 'error' ? '#dc3545' : 
                  customAlert.type === 'success' ? '#28a745' : 
                  customAlert.type === 'warning' ? '#ffc107' : 
                  '#17a2b8'
                }`
              }}
            >
              <div 
                className={`d-flex align-items-center justify-content-center me-3`}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: customAlert.type === 'error' ? 'rgba(220, 53, 69, 0.15)' : 
                                   customAlert.type === 'success' ? 'rgba(40, 167, 69, 0.15)' : 
                                   customAlert.type === 'warning' ? 'rgba(255, 193, 7, 0.15)' : 
                                   'rgba(23, 162, 184, 0.15)',
                  flexShrink: 0,
                  boxShadow: `0 2px 8px rgba(0,0,0,0.08)`
                }}
              >
                <i 
                  className={`feather-${
                    customAlert.type === 'error' ? 'alert-circle' : 
                    customAlert.type === 'success' ? 'check-circle' : 
                    customAlert.type === 'warning' ? 'alert-triangle' : 
                    'info'
                  }`}
                  style={{ 
                    fontSize: '24px', 
                    color: customAlert.type === 'error' ? '#dc3545' : 
                           customAlert.type === 'success' ? '#28a745' : 
                           customAlert.type === 'warning' ? '#ffc107' : 
                           '#17a2b8'
                  }}
                ></i>
              </div>
              <div className="flex-grow-1">
                <h6 
                  className={`mb-1 fw-bold`}
                  style={{ fontSize: '16px', 
                           color: customAlert.type === 'error' ? '#dc3545' : 
                                  customAlert.type === 'success' ? '#28a745' : 
                                  customAlert.type === 'warning' ? '#ffc107' : 
                                  '#17a2b8'
                         }}
                >
                  {customAlert.type === 'error' ? 'Error' : 
                   customAlert.type === 'success' ? 'Success' : 
                   customAlert.type === 'warning' ? 'Warning' : 
                   'Information'}
                </h6>
                <p className="mb-0 text-dark" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                  {customAlert.message}
                </p>
              </div>
              <button
                type="button"
                className="btn-close ms-2"
                onClick={hideAlert}
                aria-label="Close"
                style={{
                  opacity: 0.8,
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              ></button>
            </div>
            <div 
              className="progress" 
              style={{ height: '4px', borderRadius: '0', backgroundColor: 'rgba(0,0,0,0.05)' }}
            >
              <div 
                className={`progress-bar ${
                  customAlert.type === 'error' ? 'bg-danger' : 
                  customAlert.type === 'success' ? 'bg-success' : 
                  customAlert.type === 'warning' ? 'bg-warning' : 
                  'bg-info'
                }`}
                role="progressbar" 
                style={{ width: '100%', animation: `progressBar ${customAlert.type === 'success' ? 4 : 5}s linear forwards` }} 
                aria-valuenow="100" 
                aria-valuemin="0" 
                aria-valuemax="100"
              ></div>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%) scale(0.9);
            opacity: 0;
          }
          to {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }
        @keyframes progressBar {
          from { width: 100%; }
          to { width: 0%; }
        }
        .nav-tabs::-webkit-scrollbar {
          height: 6px;
        }
        .nav-tabs::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .nav-tabs::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 10px;
        }
        .nav-tabs::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
      `}</style>

      <Sidebar 
        sidebarCollapsed={sidebarCollapsed} 
        setSidebarCollapsed={setSidebarCollapsed} 
        toggleSidebar={toggleSidebar}
      />

      <main className="nxl-container" style={{ paddingTop: '0' }}>
        <div className="nxl-content" style={{ paddingTop: '0' }}>
          <div className="page-header">
            <div className="page-header-left d-flex align-items-center">
              <div className="nxl-navigation-toggle me-3">
                <a href="javascript:void(0);" id="menu-mini-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'none' : 'block' }}>
                  <i className="feather-align-left"></i>
                </a>
                <a href="javascript:void(0);" id="menu-expend-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'block' : 'none' }}>
                  <i className="feather-arrow-right"></i>
                </a>
              </div>
              <div className="page-header-title">
                <h5 className="m-b-10">Documents</h5>
              </div>
              <ul className="breadcrumb">
                <li className="breadcrumb-item"><a href="/investor-dashboard">Home</a></li>
                <li className="breadcrumb-item">Documents</li>
              </ul>
            </div>
            <div className="page-header-right ms-auto d-flex align-items-center gap-3">
              {/* Search Bar */}
              <div className="position-relative" style={{ width: '200px' }}>
                <i className="feather-search position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '14px', pointerEvents: 'none' }}></i>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search project"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '32px',
                    paddingRight: searchTerm ? '28px' : '12px',
                    fontSize: '12px',
                    borderColor: '#dee2e6',
                    borderRadius: '6px',
                    height: '32px'
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="btn-close position-absolute"
                    onClick={() => setSearchTerm('')}
                    style={{
                      right: '6px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '10px',
                      opacity: 0.6,
                      width: '16px',
                      height: '16px'
                    }}
                    aria-label="Clear search"
                  ></button>
                )}
              </div>
              {loginData && (
                <div className="d-flex align-items-center gap-2">
                  <i className="feather-user text-primary"></i>
                  <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                    Welcome, {(() => {
                      if (participants.length > 0 && loginData.id) {
                        const userParticipant = participants.find(p => p.participant_id == loginData.id || p.id == loginData.id);
                        return userParticipant?.name || loginData.name || 'User';
                      }
                      return loginData.name || 'User';
                    })()}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="container-fluid py-4">
            {/* Project Navigation Tabs */}
            {projects.length > 0 && (
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-0">
                  <ul className="nav nav-tabs border-0" style={{ 
                    padding: '0.5rem 1rem',
                    borderBottom: '1px solid #dee2e6',
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: '0.25rem',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e0 transparent'
                  }}>
                    {projects.map(project => {
                      const isVisible = !project.is_special || 
                                       (project.special_participants && project.special_participants.split(',').includes(loginData?.id + '')) || 
                                       loginData?.role == 1;
                      if (!isVisible) return null;
                      
                      const isActive = selectedProject?.id === project.id;
                      
                      return (
                        <li key={project.id} className="nav-item" style={{ flexShrink: 0 }}>
                          <button
                            className={`nav-link ${isActive ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedProject(project);
                              populateDocuments(project);
                            }}
                            style={{ 
                              border: 'none',
                              background: isActive ? 'rgba(13, 110, 253, 0.1)' : 'none',
                              cursor: 'pointer',
                              padding: '0.625rem 1.25rem',
                              color: isActive ? '#0d6efd' : '#495057',
                              fontWeight: '600',
                              fontSize: '13px',
                              borderRadius: '0',
                              borderBottom: isActive ? '3px solid #0d6efd' : '3px solid transparent',
                              transition: 'all 0.3s ease',
                              position: 'relative',
                              whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.color = '#0d6efd';
                                e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.color = '#495057';
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {project.name}
                            {projectsWithDocuments.has(project.id) && (
                              <span 
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  width: '8px',
                                  height: '8px',
                                  backgroundColor: '#ff9800',
                                  borderRadius: '50%',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                }}
                              ></span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}

            {/* File Upload Section */}
            {selectedProject && loginData?.role == 1 && (
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-3">
                    <label 
                      htmlFor="document-upload" 
                      className="btn btn-primary btn-sm mb-0"
                      style={{ cursor: uploadingFile ? 'not-allowed' : 'pointer' }}
                    >
                      {uploadingFile ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <i className="feather-upload me-1"></i>Upload Document
                        </>
                      )}
                    </label>
                    <input
                      type="file"
                      id="document-upload"
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                      disabled={uploadingFile}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                    />
                    <span className="text-muted fs-12">Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV</span>
                  </div>
                </div>
              </div>
            )}

            {/* Documents Grid */}
            <div 
              className="card shadow-sm border-0"
              onDragEnter={selectedProject && loginData?.role == 1 ? handleDragEnter : undefined}
              onDragOver={selectedProject && loginData?.role == 1 ? handleDragOver : undefined}
              onDragLeave={selectedProject && loginData?.role == 1 ? handleDragLeave : undefined}
              onDrop={selectedProject && loginData?.role == 1 ? handleDrop : undefined}
              style={{
                border: isDragOver && selectedProject && loginData?.role == 1 ? '2px dashed #0d6efd' : '1px solid #dee2e6',
                backgroundColor: isDragOver && selectedProject && loginData?.role == 1 ? 'rgba(13, 110, 253, 0.05)' : 'white',
                transition: 'all 0.3s ease'
              }}
            >
              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">Project Documents</h5>
                {selectedProject && documents.length > 0 && (
                  <span className="badge bg-primary">{documents.length} document{documents.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              <div 
                className="card-body"
                style={{
                  minHeight: isDragOver && selectedProject && loginData?.role == 1 ? '200px' : 'auto',
                  position: 'relative'
                }}
              >
                {isDragOver && selectedProject && loginData?.role == 1 && (
                  <div 
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(13, 110, 253, 0.05)',
                      zIndex: 10,
                      pointerEvents: 'none'
                    }}
                  >
                    <i className="feather-upload" style={{ fontSize: '48px', color: '#0d6efd' }}></i>
                    <p className="mt-3 mb-0 fw-semibold text-primary">Drop your file here</p>
                    <p className="text-muted fs-12 mt-1">Release to upload</p>
                  </div>
                )}
                {selectedProject ? (
                  loadingDocuments ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-muted mb-0 fw-semibold">Loading documents for {selectedProject.name}</p>
                      <p className="text-muted fs-12 mt-2">Please wait while we fetch the documents</p>
                    </div>
                  ) : documents.length > 0 ? (
                    <div className="row g-3">
                      {documents.filter(doc => doc && (doc.document_url || doc.url || doc.file_url || doc.file_path)).map((doc, index) => {
                        const fileName = doc.document_name || doc.name || doc.file_name || doc.filename || 'Untitled';
                        const fileUrl = doc.document_url || doc.url || doc.file_url || doc.file_path || '';
                        
                        const getFileIcon = (fileName) => {
                          if (!fileName) return 'file';
                          const ext = fileName.split('.').pop()?.toLowerCase();
                          if (['pdf'].includes(ext)) return 'file-text';
                          if (['doc', 'docx'].includes(ext)) return 'file';
                          if (['xls', 'xlsx'].includes(ext)) return 'file-text';
                          if (['ppt', 'pptx'].includes(ext)) return 'file-text';
                          if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext)) return 'image';
                          if (['zip', 'rar', '7z'].includes(ext)) return 'archive';
                          return 'file';
                        };

                        const getFileColor = (fileName) => {
                          if (!fileName) return '#6c757d';
                          const ext = fileName.split('.').pop()?.toLowerCase();
                          if (['pdf'].includes(ext)) return '#dc3545';
                          if (['doc', 'docx'].includes(ext)) return '#0d6efd';
                          if (['xls', 'xlsx'].includes(ext)) return '#198754';
                          if (['ppt', 'pptx'].includes(ext)) return '#fd7e14';
                          if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext)) return '#6f42c1';
                          return '#6c757d';
                        };
                        
                        return (
                          <div key={doc.id || index} className="col-md-4 col-lg-3 col-sm-6">
                            <div 
                              className="card h-100 border shadow-sm"
                              style={{
                                transition: 'all 0.3s ease',
                                cursor: 'pointer'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                              }}
                            >
                              <div className="card-body p-3">
                                <div className="d-flex flex-column align-items-center text-center mb-3">
                                  <div 
                                    className="d-flex align-items-center justify-content-center rounded mb-2"
                                    style={{
                                      width: '64px',
                                      height: '64px',
                                      backgroundColor: `${getFileColor(fileName)}15`,
                                      border: `2px solid ${getFileColor(fileName)}40`
                                    }}
                                  >
                                    <i 
                                      className={`feather-${getFileIcon(fileName)}`}
                                      style={{ 
                                        fontSize: '32px',
                                        color: getFileColor(fileName)
                                      }}
                                    ></i>
                                  </div>
                                  <a 
                                    href={fileUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-dark text-decoration-none fw-semibold"
                                    style={{ 
                                      fontSize: '13px',
                                      lineHeight: '1.3',
                                      wordBreak: 'break-word',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {fileName || 'Untitled Document'}
                                  </a>
                                </div>
                                
                                <div className="d-flex flex-column gap-2">
                                  {(doc.document_type || doc.type) && (doc.document_type || doc.type).toLowerCase() !== 'other' && (
                                    <div>
                                      <span className="badge bg-light text-dark fs-11 w-100 d-block text-center">
                                        {doc.document_type || doc.type}
                                      </span>
                                    </div>
                                  )}
                                  
                                  <div className="d-flex align-items-center justify-content-center gap-2 text-muted fs-11">
                                    <i className="feather-calendar" style={{ fontSize: '10px' }}></i>
                                    <span>
                                      {(doc.created_at || doc.created_date) ? new Date(doc.created_at || doc.created_date).toLocaleDateString() : '-'}
                                    </span>
                                  </div>

                                  <div className="d-flex gap-2 justify-content-center mt-2">
                                    <a 
                                      href={fileUrl} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline-primary"
                                      title="View/Download"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ fontSize: '11px', padding: '4px 12px' }}
                                    >
                                      <i className="feather-eye me-1" style={{ fontSize: '12px' }}></i>
                                      View
                                    </a>
                                    {loginData?.role == 1 && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteClick(doc);
                                        }}
                                        title={deletingDocumentId === (doc.id || doc.file_id || doc.document_id) ? 'Deleting...' : 'Delete'}
                                        disabled={deletingDocumentId === (doc.id || doc.file_id || doc.document_id)}
                                        style={{ 
                                          fontSize: '11px', 
                                          padding: '4px 12px',
                                          opacity: deletingDocumentId === (doc.id || doc.file_id || doc.document_id) ? 0.6 : 1,
                                          cursor: deletingDocumentId === (doc.id || doc.file_id || doc.document_id) ? 'not-allowed' : 'pointer'
                                        }}
                                      >
                                        {deletingDocumentId === (doc.id || doc.file_id || doc.document_id) ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" style={{ width: '10px', height: '10px' }}></span>
                                            <span style={{ fontSize: '10px' }}>Deleting</span>
                                          </>
                                        ) : (
                                          <i className="feather-trash-2" style={{ fontSize: '12px' }}></i>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      <i className="feather-inbox fs-1 mb-2 d-block"></i>
                      <p className="mb-0">No documents found for this project</p>
                      {loginData?.role == 1 && (
                        <p className="mt-2 fs-12">Upload a document using the button above</p>
                      )}
                    </div>
                  )
                ) : (
                  <div className="text-center py-5 text-muted">
                    <i className="feather-folder fs-1 mb-2 d-block"></i>
                    Loading all available projects
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
          onClick={handleDeleteCancel}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: '500px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 pb-0">
                <div className="d-flex align-items-center gap-3 w-100">
                  <div 
                    className="d-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626'
                    }}
                  >
                    <i className="feather-alert-triangle" style={{ fontSize: '24px' }}></i>
                  </div>
                  <div className="flex-grow-1">
                    <h5 className="modal-title mb-0 fw-bold">Delete Document</h5>
                    <p className="text-muted mb-0 fs-12 mt-1">This action cannot be undone</p>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleDeleteCancel}
                    disabled={deletingDocumentId !== null}
                    aria-label="Close"
                    style={{ opacity: deletingDocumentId !== null ? 0.5 : 1, cursor: deletingDocumentId !== null ? 'not-allowed' : 'pointer' }}
                  ></button>
                </div>
              </div>
              <div className="modal-body pt-3">
                <p className="mb-0" style={{ wordBreak: 'break-word' }}>
                  Are you sure you want to delete <strong style={{ wordBreak: 'break-word' }}>"{deleteConfirmation.document?.document_name || 'this document'}"</strong>?
                </p>
                <p className="text-muted fs-12 mt-2 mb-0">
                  This document will be permanently removed from the project.
                </p>
              </div>
              <div className="modal-footer border-0 pt-0 d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleDeleteCancel}
                  disabled={deletingDocumentId !== null}
                  style={{ 
                    minWidth: '80px',
                    opacity: deletingDocumentId !== null ? 0.6 : 1,
                    cursor: deletingDocumentId !== null ? 'not-allowed' : 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={handleDeleteConfirm}
                  disabled={deletingDocumentId !== null}
                  style={{ 
                    minWidth: '80px',
                    opacity: deletingDocumentId !== null ? 0.6 : 1,
                    cursor: deletingDocumentId !== null ? 'not-allowed' : 'pointer'
                  }}
                >
                  {deletingDocumentId !== null ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" style={{ width: '12px', height: '12px' }}></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="feather-trash-2 me-1" style={{ fontSize: '14px' }}></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Documents;

